import type { ModelKey } from '../world/Assets';

export type Slot = 'weapon' | 'shield' | 'none';
export type ItemId =
  | 'dagger' | 'sword' | 'axe' | 'greatsword' | 'waraxe'
  | 'shieldRound' | 'shieldSquare' | 'shieldSpikes' | 'shieldCrest'
  | 'crossbow' | 'quiver' | 'smokebomb' | 'wand' | 'spellbook'
  | 'bun' | 'cider' | 'purse' | 'ingots' | 'songsheet' | 'turnips' | 'lostquiver' | 'banner' | 'lantern';

export type Item = {
  id: ItemId;
  name: string;
  price: number;
  model: ModelKey;
  slot: Slot;
  blurb: string;
  /** Quest items can't be bought and don't show in shops. */
  quest?: boolean;
  stacks?: boolean;
};

export const ITEMS: Record<ItemId, Item> = {
  dagger: { id: 'dagger', name: 'Pocket Dagger', price: 10, model: 'dagger', slot: 'weapon', blurb: 'Small, sharp, easy to hide.' },
  sword: { id: 'sword', name: 'Iron Sword', price: 25, model: 'sword', slot: 'weapon', blurb: 'The classic. Balanced and honest.' },
  axe: { id: 'axe', name: 'Hand Axe', price: 22, model: 'axe', slot: 'weapon', blurb: 'Chops wood. Chops other things.' },
  greatsword: { id: 'greatsword', name: 'Steel Greatsword', price: 70, model: 'greatsword', slot: 'weapon', blurb: 'Two hands. Zero doubts.' },
  waraxe: { id: 'waraxe', name: 'War Axe', price: 60, model: 'waraxe', slot: 'weapon', blurb: 'Heavy enough to end arguments.' },
  shieldRound: { id: 'shieldRound', name: 'Round Shield', price: 18, model: 'shieldRound', slot: 'shield', blurb: 'Wood and iron. Takes a hit.' },
  shieldSquare: { id: 'shieldSquare', name: 'Tower Shield', price: 32, model: 'shieldSquare', slot: 'shield', blurb: 'A wall you carry around.' },
  shieldSpikes: { id: 'shieldSpikes', name: 'Spiked Shield', price: 45, model: 'shieldSpikes', slot: 'shield', blurb: 'Defence, but rude about it.' },
  shieldCrest: { id: 'shieldCrest', name: 'Burnwood Crest', price: 90, model: 'shieldBadge', slot: 'shield', blurb: 'Carry the colours. People notice.' },
  crossbow: { id: 'crossbow', name: 'Crossbow', price: 35, model: 'crossbow', slot: 'weapon', blurb: 'Point. Click. Problem solved.' },
  quiver: { id: 'quiver', name: 'Bolt Quiver', price: 8, model: 'quiver', slot: 'none', blurb: 'Twenty bolts and a strap.', stacks: true },
  smokebomb: { id: 'smokebomb', name: 'Smoke Bomb', price: 6, model: 'smokebomb', slot: 'none', blurb: 'For dramatic exits.', stacks: true },
  wand: { id: 'wand', name: 'Ember Wand', price: 40, model: 'wand', slot: 'weapon', blurb: 'Warm to the touch. Don’t point it at hay.' },
  spellbook: { id: 'spellbook', name: 'Spellbook', price: 50, model: 'spellbook', slot: 'none', blurb: 'Mostly recipes. Some of them explode.' },
  bun: { id: 'bun', name: 'Sweet Bun', price: 2, model: 'mug', slot: 'none', blurb: 'Still warm.', stacks: true },
  cider: { id: 'cider', name: 'Mug of Cider', price: 3, model: 'mug', slot: 'none', blurb: 'Fizzy. Questionable.', stacks: true },
  purse: { id: 'purse', name: 'Embroidered Purse', price: 0, model: 'coinsSmall', slot: 'none', blurb: 'Not yours. Heavy, though.', quest: true },
  ingots: { id: 'ingots', name: 'Crate of Ingots', price: 0, model: 'box', slot: 'none', blurb: 'For the forge. Very heavy.', quest: true },
  songsheet: { id: 'songsheet', name: 'Song Sheet', price: 0, model: 'spellbook', slot: 'none', blurb: 'Lyrics, mostly crossed out.', quest: true, stacks: true },
  turnips: { id: 'turnips', name: 'Crate of Turnips', price: 0, model: 'crateCarrots', slot: 'none', blurb: 'Odo calls them turnips. They are carrots.', quest: true, stacks: true },
  lostquiver: { id: 'lostquiver', name: 'Fern’s Quiver', price: 0, model: 'quiver', slot: 'none', blurb: 'Muddy, but every arrow is there.', quest: true },
  banner: { id: 'banner', name: 'The Keep’s Banner', price: 0, model: 'bannerRed', slot: 'none', blurb: 'A thousand years old. Smells like it.', quest: true },
  lantern: { id: 'lantern', name: 'Miner’s Lantern', price: 0, model: 'torch', slot: 'none', blurb: 'Still warm.', quest: true },
};

export type Shop = { id: string; name: string; stock: ItemId[] };

export const SHOPS: Record<string, Shop> = {
  forge: { id: 'forge', name: 'Ironhand Forge', stock: ['dagger', 'sword', 'axe', 'greatsword', 'waraxe'] },
  shields: { id: 'shields', name: 'Corwin’s Shields', stock: ['shieldRound', 'shieldSquare', 'shieldSpikes', 'shieldCrest'] },
  fletcher: { id: 'fletcher', name: 'Pip’s Bolts', stock: ['crossbow', 'quiver'] },
  alchemist: { id: 'alchemist', name: 'Ysolde’s Oddities', stock: ['smokebomb', 'wand', 'spellbook'] },
  bakery: { id: 'bakery', name: 'Tobin’s Ovens', stock: ['bun', 'cider'] },
};

type Listener = () => void;

export class Wallet {
  coins: number;
  private readonly bag = new Map<ItemId, number>();
  equipped: { weapon: ItemId | null; shield: ItemId | null } = { weapon: null, shield: null };
  private readonly listeners: Listener[] = [];
  /** Fired with what changed, for toasts and quest checks. */
  onEvent: (e: { type: 'buy' | 'gain' | 'lose' | 'coins' | 'equip'; item?: ItemId; amount?: number }) => void = () => {};

  constructor(start = 5) {
    this.coins = start;
  }

  subscribe(l: Listener): void {
    this.listeners.push(l);
  }

  private changed(): void {
    for (const l of this.listeners) l();
  }

  count(id: ItemId): number {
    return this.bag.get(id) ?? 0;
  }

  has(id: ItemId): boolean {
    return this.count(id) > 0;
  }

  hasSlot(slot: Slot): boolean {
    return [...this.bag.keys()].some((id) => ITEMS[id].slot === slot);
  }

  items(): { item: Item; count: number }[] {
    return [...this.bag.entries()].filter(([, n]) => n > 0).map(([id, count]) => ({ item: ITEMS[id], count }));
  }

  owns(id: ItemId): boolean {
    return this.has(id) && !ITEMS[id].stacks;
  }

  buy(id: ItemId): 'ok' | 'poor' | 'owned' {
    const item = ITEMS[id];
    if (this.owns(id)) return 'owned';
    if (this.coins < item.price) return 'poor';
    this.coins -= item.price;
    this.bag.set(id, this.count(id) + 1);
    // Auto-equip a first weapon/shield so you see it straight away.
    if (item.slot === 'weapon' && !this.equipped.weapon) this.equipped.weapon = id;
    if (item.slot === 'shield' && !this.equipped.shield) this.equipped.shield = id;
    this.onEvent({ type: 'buy', item: id, amount: item.price });
    this.changed();
    return 'ok';
  }

  give(id: ItemId, n = 1): void {
    this.bag.set(id, this.count(id) + n);
    this.onEvent({ type: 'gain', item: id, amount: n });
    this.changed();
  }

  take(id: ItemId, n = 1): boolean {
    if (this.count(id) < n) return false;
    this.bag.set(id, this.count(id) - n);
    if (this.equipped.weapon === id && !this.has(id)) this.equipped.weapon = null;
    if (this.equipped.shield === id && !this.has(id)) this.equipped.shield = null;
    this.onEvent({ type: 'lose', item: id, amount: n });
    this.changed();
    return true;
  }

  earn(n: number): void {
    this.coins += n;
    this.onEvent({ type: 'coins', amount: n });
    this.changed();
  }

  equip(id: ItemId): void {
    const slot = ITEMS[id].slot;
    if (slot === 'weapon') this.equipped.weapon = this.equipped.weapon === id ? null : id;
    if (slot === 'shield') this.equipped.shield = this.equipped.shield === id ? null : id;
    this.onEvent({ type: 'equip', item: id });
    this.changed();
  }
}
